import { useMemo } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { type IChipProps, type IDropdownItem, Typography, EnumDropdownItemVariant } from 'shared/ui'
import { Select } from 'shared/ui/Select/Select'
import { InboxCardContent } from 'entities/Inbox/ui/InboxDropdownCard/InboxDropdownCard'
import { type RowInviteCandidate } from 'entities/Users/model/Table/RowInviteCandidate'
import { EnumUserRoles } from 'entities/Users/model/types'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { InboxChip } from 'entities/Inbox/ui/InboxChip/InboxChip'
import { InboxDropdownContent } from './InboxDropdownContent'
import { NEW_INBOX_ID } from '../../../lib/constants'
import styles from './styles.module.scss'
const newInboxOption: IDropdownItem = {
  id: NEW_INBOX_ID,
  label: 'New Inbox',
}

const InboxChipWithNewInbox = ({ item, label, onDelete }: IChipProps) => {
  if (item?.id === NEW_INBOX_ID) {
    return (
      <Typography
        ariaLabel='NewInboxText'
        variant='text-input-field-r'
        className={styles.newInboxLabel}
      >
        {label}
      </Typography>
    )
  }

  return <InboxChip item={item} label={label} iconType='div' onDelete={onDelete} />
}

type InboxesSelectProps = {
  row: RowInviteCandidate
  inboxes: Inbox[]
  disabled?: boolean
  isNewInboxHidden?: boolean
}

export const InboxesSelect = observer(
  ({ row, inboxes, disabled, isNewInboxHidden }: InboxesSelectProps) => {
    const rowSelectedInboxes = row.getSelectedInboxes(inboxes)

    const isObserverRole = row.role === EnumUserRoles.VIEW_ONLY
    const hideNewInbox = isObserverRole || isNewInboxHidden

    const inboxItems = useMemo<IDropdownItem[]>(() => {
      const inboxOptions = inboxes.map((inbox) => ({
        id: inbox.id.toString(),
        label: inbox.name,
        renderOption: () => <InboxCardContent item={inbox} />,
      }))

      if (hideNewInbox) {
        return inboxOptions
      }

      const newInboxWithSelection: IDropdownItem = {
        ...newInboxOption,
        activeValue: row.isNewInbox,
        variant: EnumDropdownItemVariant.CheckedRight,
      }

      return [newInboxWithSelection, ...inboxOptions]
    }, [inboxes, row.isNewInbox, hideNewInbox])

    const selectedItems = useMemo<IDropdownItem[]>(() => {
      if (row.isNewInbox) {
        return [{ id: NEW_INBOX_ID, label: 'New Inbox' }]
      }
      return rowSelectedInboxes.map((inbox) => ({
        id: inbox.id.toString(),
        label: inbox.name,
      }))
    }, [row, rowSelectedInboxes])

    const handleToggleInbox = (item: IDropdownItem) => {
      if (item.id === NEW_INBOX_ID) {
        row.setNewInbox()
      } else {
        row.toggleInbox(Number(item.id))
      }
    }

    const hasSelectedItems = selectedItems.length > 0

    return (
      <Select
        type='multiList'
        text='Inboxes'
        items={inboxItems}
        selectedItems={selectedItems}
        onSelect={handleToggleInbox}
        onDelete={handleToggleInbox}
        error={row.hasInboxError}
        withInitialSearch
        ChipComponent={InboxChipWithNewInbox}
        placeholderClassName={classNames(
          styles.selectPlaceholder,
          hasSelectedItems && styles.withChipPadding
        )}
        dropdownProps={{
          width: '267px',
          disabled,
          textFieldProps: {
            size: 'small',
          },
          PopperProps: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  offset: ({ reference }) => {
                    return [-2, -reference.height - 8]
                  },
                },
              },
            ],
          },
        }}
        customComponent={(onCloseMenu) => (
          <InboxDropdownContent
            ChipComponent={InboxChipWithNewInbox}
            items={inboxItems}
            selectedItems={selectedItems}
            onSelect={handleToggleInbox}
            onDelete={handleToggleInbox}
            onCloseMenu={onCloseMenu}
          />
        )}
      />
    )
  }
)
